import { usePostsQuery, SortOrder } from "../../../../generated/graphql";
import styled, { css } from "styled-components";
import "./index.css";
import { ResetContainer } from "./cssReset.js";

const Container = styled(ResetContainer)`
  &&& {
    font-family: "Roboto", sans-serif;
    font-size: 14px;
    background-color: ${(props) => props.secondaryColor};
    border-radius: 5px;
    box-shadow: 0 5px 15px hsla(0, 0%, 81.6%, 0.17);
    border-top: 10px solid ${(props) => props.primaryColor};
    border-bottom: 10px solid ${(props) => props.primaryColor};
    border-radius: 8px 8px 0 0;
    padding-bottom: 10px;

    h2.widgetHeading {
      font-weight: 900;
      font-size: 24px;
      word-wrap: break-word;
      margin: 0;
      padding: 0;
      padding: 12px;
    }

    a {
      color: #000;
      text-decoration: none;

      &:hover: {
        text-decoration: none;
      }
    }
  }
`;

const HeroPostContainer = styled.div`
  &&& {
    width: 100%;

    div {
      margin: 16px 8px 8px;
    }

    img {
      width: 100%;
    }

    h1 {
      color: #333;
      font-weight: 900;
      font-size: 48px;
      word-wrap: break-word;
      margin-bottom: 8px;
      line-height: 1.1;
      margin: 5px 0;

      @media (max-width: 767px) {
        word-wrap: break-word;
        font-size: 27px;
        line-height: 1.1em;
      }
    }

    span {
      color: #333;

      @media screen and (max-width: 747px) {
        display: -webkit-box !important;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        padding: 0 !important;
        overflow: hidden;
      }
    }
  }
`;

const AdditionalPostsContainer = styled.div`
  &&& {
    display: flex;
    flex-direction: column;

    @media only screen and (min-width: 748px) {
      flex-direction: row;
      padding: 0 10px;
    }
  }
`;

const PostContainer = styled.a`
  &&& {
    width: 100%;
    display: flex;
    flex-direction: row;
    padding: 5px 0;
    align-items: center;

    @media only screen and (min-width: 748px) {
      flex-direction: column;
      width: 50%;
      padding: 0 5px;
      span {
        display: block;
      }
    }

    img {
      width: 100px !important;
      height: 100px;
      object-fit: cover;
      margin-right: 10px;

      @media only screen and (min-width: 748px) {
        width: 100% !important;
        height: auto;
        margin-bottom: 10px;
      }
    }

    div {
      width: calc(100% - 120px);

      @media screen and (min-width: 747px) {
        width: 100%;
      }
    }

    h2 {
      color: #333;
      font-weight: 900;
      font-size: 24px;
      margin-bottom: 8px;
      line-height: 1.1;
      margin: 5px 0;

      @media screen and (max-width: 747px) {
        font-size: 19px !important;
        display: -webkit-box !important;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        padding: 0 !important;
        overflow: hidden;
      }
    }

    span {
      color: #333;
      display: -webkit-box !important;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      padding: 0 !important;
      overflow: hidden;
    }
  }
`;

const Divider = styled.div`
  &&& {
    width: calc(100% - 20px);
    border-top: 1px solid #dfdfdf;
    margin: 8px 10px;
  }
`;

const CrosslinkWidget = ({ ...props }) => {
  const { site: site, title = "" } = props || {};
  let url = site;
  if (!url.includes("https://") && !url.includes("http://")) {
    url = `https://${url}`;
  }

  const skip = 2;
  const count = 3;

  let { data: { posts: pinnedPostsData = [] } = {} } = usePostsQuery({
    variables: {
      where: {
        site: {
          url: {
            equals: url,
          },
        },
        position: {
          gte: skip,
        },
        date: {
          not: null
        },
        status: {
          equals: "publish"
        }
      },
      take: count,
      orderBy: [{
        position: SortOrder.Asc,
      }],
    },
  });

  let { data: { posts: latestPostsData = [] } = {} } = usePostsQuery({
    variables: {
      where: {
        site: {
          url: {
            equals: url,
          },
        },
        date: {
          not: null
        },
        position: null,
        status: {
          equals: "publish"
        }
      },
      orderBy: [{
        date: SortOrder.Asc,
      }],
      take: -count,
      skip: skip,
    },
  });


  //Combine latest and pinned posts
  const pinnedPosts = JSON.parse(JSON.stringify(pinnedPostsData));
  const latestPosts = JSON.parse(JSON.stringify(latestPostsData)).reverse();

  const posts = Array(count)
    .fill(undefined)
    .map((slot, i) => {
      const post = pinnedPosts.find((post) => post?.position === i + skip);
      if (post) {
        return post;
      } else {
        return latestPosts.shift();
      }
    })
    .filter((post) => post);

  if (!posts || posts.length < 3) {
    return;
  }

  const _site = posts?.[0]?.site ?? {};
  const { primary = "#fff", secondary = "#fff" } = _site?.colors ?? {};

  return (
    <Container
      primaryColor={primary}
      secondaryColor={secondary}
      className="newsnerWidget"
    >
      <h2 className="widgetHeading">{title}</h2>

      <HeroPostContainer>
        <a href={posts[0].link} target="_blank">
          <img
            loading="lazy"
            src={posts[0].featuredMedia && posts[0].featuredMedia.sourceUrl}
          />
          <div>
            <h1>{posts[0].title}</h1>
            <span>{posts[0].excerpt}</span>
          </div>
        </a>
      </HeroPostContainer>

      <Divider />

      <AdditionalPostsContainer>
        {[posts[1], posts[2]].map((post, i) => (
          <PostContainer href={post.link} target="_blank">
            <img loading="lazy" src={post.featuredMedia && post.featuredMedia.sourceUrl} />
            <div>
              <h2>{post.title}</h2>
              <span>{post.excerpt}</span>
            </div>
          </PostContainer>
        ))}
      </AdditionalPostsContainer>
    </Container>
  );
};

export default CrosslinkWidget;
